import { useRouteContext } from "@tanstack/react-router";
import { Fragment, useTransition } from "react";
import {
	ConnectionHandler,
	type FetchPolicy,
	graphql,
	useFragment,
	useMutation,
} from "react-relay";
import { toast } from "sonner";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import {
	MobileReplyProvider,
	useMobileReply,
} from "@/contexts/mobile-reply-context";
import { AnalyticsEvents, useAnalytics } from "@/utils/analytics";
import type { commentListCreateMutation } from "./__generated__/commentListCreateMutation.graphql";
import type { commentListFragment$key } from "./__generated__/commentListFragment.graphql";
import type { commentListReplyMutation } from "./__generated__/commentListReplyMutation.graphql";
import type { CommentOrdering } from "./__generated__/postCommentsQuery.graphql";
import { ParentComment } from "./comment";
import { CommentComposer, MobileCommentComposer } from "./comment-composer";

type CommentListProps = {
	parentId?: string;
	orderBy: CommentOrdering;
	setOrderBy: (val: CommentOrdering) => void;
	loadNext: () => void;
	refetch: (
		{ orderBy }: { orderBy: CommentOrdering },
		opts?: { fetchPolicy?: FetchPolicy },
	) => void;
	hasNext: boolean;
	comments: commentListFragment$key;
	entity: "post" | "lesson";
	entityId: string;
	commentCount: number;
};

export function CommentList({
	parentId,
	orderBy,
	setOrderBy,
	loadNext,
	comments,
	refetch,
	hasNext,
	entity,
	entityId,
	commentCount,
}: CommentListProps) {
	const { session } = useRouteContext({ from: "__root__" });
	const { track } = useAnalytics();
	const data = useFragment<commentListFragment$key>(
		graphql`
    fragment commentListFragment on CommentInterfaceConnection {
        edges {
          node {
            id
            ...comment_parentComment

            __typename
          }
          cursor
        }
    }
  `,
		comments,
	);

	const [isPending, startTransition] = useTransition();

	const [addComment, isPendingAddComment] =
		useMutation<commentListCreateMutation>(graphql`
	  mutation commentListCreateMutation($payload: CreateCommentInput!, $connections: [ID!]!) {
			  registerComment(payload: $payload) {
					commentEdge @prependEdge(connections: $connections) {
					  node {
               id
               ...comment_parentComment
            }
            cursor
           }
         }
			}
	`);

	const connectionKey =
		entity === "post"
			? "postComments_post_comments"
			: "lessonComments_comments";

	const entityConnections = [
		"BEST",
		"LEAST_UPVOTED",
		"MOST_UPVOTED",
		"NEWEST",
		"OLDEST",
		"TRENDING",
	].map((order) =>
		ConnectionHandler.getConnectionID(entityId, connectionKey, {
			orderBy: order,
		}),
	);
	const parentConnections = parentId
		? [
				"BEST",
				"LEAST_UPVOTED",
				"MOST_UPVOTED",
				"NEWEST",
				"OLDEST",
				"TRENDING",
			].map((order) =>
				ConnectionHandler.getConnectionID(parentId, connectionKey, {
					orderBy: order,
				}),
			)
		: [];

	const connections = [...entityConnections, ...parentConnections];

	return (
		<MobileReplyProvider>
			<Card className="flex max-md:px-3 flex-col gap-6 max-md:gap-3 items-center max-md:border-0 max-md:shadow-none max-w-full">
				<CardHeader className="flex w-full flex-row items-center justify-between max-md:px-0">
					<CardTitle>Comentários ({commentCount})</CardTitle>

					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost">
								<span className="max-md:hidden">
									{orderBy === "TRENDING" ? "Mais Relevante Primeiro" : null}
									{orderBy === "NEWEST" ? "Mais Recentes Primeiro" : null}
									{orderBy === "OLDEST" ? "Mais Antigos Primeiro" : null}
								</span>

								<span className="md:hidden">
									{orderBy === "TRENDING" ? "+ Relevante" : null}
									{orderBy === "NEWEST" ? "+ Recentes" : null}
									{orderBy === "OLDEST" ? "+ Antigos" : null}
								</span>
								<Icon name="ChevronDown" className="size-4" />
							</Button>
						</DropdownMenuTrigger>

						<DropdownMenuContent>
							{orderBy !== "TRENDING" ? (
								<DropdownMenuItem onClick={() => setOrderBy("TRENDING")}>
									Mais Relevates Primeiro
								</DropdownMenuItem>
							) : null}
							{orderBy !== "NEWEST" ? (
								<DropdownMenuItem onClick={() => setOrderBy("NEWEST")}>
									Mais Recentes Primeiro
								</DropdownMenuItem>
							) : null}
							{orderBy !== "OLDEST" ? (
								<DropdownMenuItem onClick={() => setOrderBy("OLDEST")}>
									Mais Antigos Primeiro
								</DropdownMenuItem>
							) : null}
						</DropdownMenuContent>
					</DropdownMenu>
				</CardHeader>

				{session?.user ? (
					<div className="px-6 w-full max-md:hidden">
						<CommentComposer
							isLoading={isPendingAddComment}
							onSubmit={(content) =>
								new Promise((resolve, reject) => {
									addComment({
										variables: {
											payload: {
												content: JSON.stringify(content),
												parent: parentId,
												courseClass: entity === "lesson" ? entityId : undefined,
												post: entity === "post" ? entityId : undefined,
											},
											connections,
										},
										onCompleted: (data) => {
											toast.success("Comentário adicionado com sucesso!");

											resolve(data);
										},
										updater: (store) => {
											connections.map((c) => store.get(c)?.invalidateRecord());

											if (!data.edges.length) {
												startTransition(() => {
													refetch({ orderBy }, { fetchPolicy: "network-only" });
												});
											}
										},
										onError: reject,
									});
								})
							}
						/>
					</div>
				) : null}

				<Separator />

				<div className="px-6 w-full flex flex-col gap-6 max-md:px-3">
					{isPending ? (
						<div className="w-full flex items-center justify-center">
							<Spinner />
						</div>
					) : data.edges.filter(Boolean).length ? (
						data.edges.filter(Boolean).map(({ node, cursor }) => (
							<Fragment key={cursor}>
								<ParentComment comment={node} />

								<Separator className="last:hidden" />
							</Fragment>
						))
					) : (
						<div className="flex w-full items-center justify-center">
							<span className="text-center text-muted-foreground">
								Seja o primeiro a comentar
							</span>
						</div>
					)}

					{hasNext && (
						<Button
							variant="outline"
							className="w-fit mt-3"
							onClick={() => loadNext()}
						>
							Ver Mais
						</Button>
					)}
				</div>
			</Card>

			<MobileCommentSection
				entity={entity}
				entityId={entityId}
				parentId={parentId}
				connections={connections}
				addComment={addComment}
				isPendingAddComment={isPendingAddComment}
				orderBy={orderBy}
				hasEdges={!!data.edges.length}
				refetch={refetch}
				startTransition={startTransition}
				track={track}
			/>
		</MobileReplyProvider>
	);
}

function MobileCommentSection({
	entity,
	entityId,
	parentId,
	connections,
	addComment,
	isPendingAddComment,
	orderBy,
	hasEdges,
	refetch,
	startTransition,
	track,
}: {
	entity: "post" | "lesson";
	entityId: string;
	parentId?: string;
	connections: string[];
	addComment: ReturnType<typeof useMutation<commentListCreateMutation>>[0];
	isPendingAddComment: boolean;
	orderBy: CommentOrdering;
	hasEdges: boolean;
	refetch: CommentListProps["refetch"];
	startTransition: (cb: () => void) => void;
	track: ReturnType<typeof useAnalytics>["track"];
}) {
	const { replyTarget, clearReplyTarget } = useMobileReply();

	const [addReply, isPendingReply] =
		useMutation<commentListReplyMutation>(graphql`
		mutation commentListReplyMutation($payload: CreateCommentInput!, $connections: [ID!]!) {
			registerComment(payload: $payload) {
				commentEdge @prependEdge(connections: $connections) {
					node {
						id
						...commentFragment
					}
					cursor
				}
			}
		}
	`);

	return (
		<MobileCommentComposer
			isLoading={isPendingAddComment || isPendingReply}
			replyTarget={replyTarget}
			onCancelReply={clearReplyTarget}
			onSubmit={(content) => {
				if (replyTarget) {
					return new Promise((resolve, reject) => {
						addReply({
							variables: {
								payload: {
									parent: replyTarget.parentCommentId,
									content: JSON.stringify(content),
								},
								connections: replyTarget.connections,
							},
							updater: (store) => {
								replyTarget.connections.map((c) =>
									store.get(c)?.invalidateRecord(),
								);
							},
							onCompleted: (response) => {
								track(AnalyticsEvents.COMMENT_REPLY_CREATED, {
									parent_comment_id: replyTarget.parentCommentId,
								});
								toast.success("Resposta adicionada com sucesso!");
								replyTarget.refetch();
								clearReplyTarget();
								resolve(response);
							},
							onError: reject,
						});
					});
				}

				return new Promise((resolve, reject) => {
					addComment({
						variables: {
							payload: {
								content: JSON.stringify(content),
								parent: parentId,
								courseClass: entity === "lesson" ? entityId : undefined,
								post: entity === "post" ? entityId : undefined,
							},
							connections,
						},
						onCompleted: (data) => {
							track(AnalyticsEvents.COMMENT_CREATED, {
								parent_type: entity,
								parent_id: entityId,
							});
							toast.success("Comentário adicionado com sucesso!");
							resolve(data);
						},
						updater: (store) => {
							connections.map((c) => store.get(c)?.invalidateRecord());

							if (!hasEdges) {
								startTransition(() => {
									refetch({ orderBy }, { fetchPolicy: "network-only" });
								});
							}
						},
						onError: reject,
					});
				});
			}}
		/>
	);
}
